// System notifications for the PWA.
//
// These are LOCAL notifications: the app raises them itself for events it
// observes while running (sign-in request received, approval finished, errors).
// They are most useful once the PWA is installed, where the app can be in the
// background while a sign-in is in flight.
//
// This is deliberately NOT Web Push - remote push (waking the app when it is
// fully closed) needs a push service plus a VAPID key pair and a server that
// stores subscriptions and sends messages. Nothing in the Tide stack issues
// those today. `subscribeToPush()` below is the seam where that would attach.
//
// Notifications are shown through the service worker registration when one is
// available, because installed PWAs on Android only allow SW-shown
// notifications; a plain `new Notification()` throws there.

import { Storage } from "./StorageManager.js";

const ENABLED_KEY = "notifications_enabled";

let _enabled = false;

function supported() {
  return typeof window !== "undefined" && "Notification" in window;
}

function permission() {
  return supported() ? Notification.permission : "unsupported";
}

async function swRegistration() {
  try {
    if (!("serviceWorker" in navigator)) return null;
    return (await navigator.serviceWorker.getRegistration()) || null;
  } catch {
    return null;
  }
}

const Notifications = {
  isSupported: supported,
  permission,

  /** Has the user turned notifications on in Settings? */
  isEnabled() {
    return _enabled && permission() === "granted";
  },

  /** Load the stored preference. Call once on boot. */
  async load() {
    try {
      _enabled = (await Storage.get(ENABLED_KEY)) === "true";
    } catch {
      _enabled = false;
    }
    return _enabled;
  },

  /**
   * Turn notifications on/off. Turning on prompts for permission if needed.
   * @returns {Promise<{enabled: boolean, permission: string}>}
   */
  async setEnabled(enabled) {
    if (!enabled) {
      _enabled = false;
      await Storage.set(ENABLED_KEY, "false");
      return { enabled: false, permission: permission() };
    }

    if (!supported()) return { enabled: false, permission: "unsupported" };

    let perm = permission();
    if (perm === "default") {
      // Must be called from a user gesture (the Settings toggle/save click).
      try { perm = await Notification.requestPermission(); } catch { perm = permission(); }
    }

    _enabled = perm === "granted";
    await Storage.set(ENABLED_KEY, String(_enabled));
    return { enabled: _enabled, permission: perm };
  },

  /**
   * Show a notification, if enabled and permitted. Never throws.
   * @param {string} title
   * @param {{ body?: string, tag?: string, requireInteraction?: boolean, silent?: boolean, data?: any }} [options]
   */
  async show(title, options = {}) {
    if (!this.isEnabled()) return false;
    // Pointless (and noisy) to notify about something the user is looking at.
    if (typeof document !== "undefined" && document.visibilityState === "visible" && !options.force) return false;

    const payload = {
      body: options.body || "",
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag: options.tag || "tide-auth",
      renotify: !!options.tag,
      requireInteraction: !!options.requireInteraction,
      silent: !!options.silent,
      data: options.data || {},
    };

    try {
      const reg = await swRegistration();
      if (reg?.showNotification) {
        await reg.showNotification(title, payload);
        return true;
      }
      // Desktop browsers without an active SW registration
      new Notification(title, payload);
      return true;
    } catch (e) {
      console.warn("[notify] failed:", e?.message || e);
      return false;
    }
  },

  /* ---------- Event helpers (keep copy in one place) ---------- */

  signInRequested(service) {
    return this.show("Sign-in request", {
      body: service ? `${service} is asking you to approve a sign-in.` : "A site is asking you to approve a sign-in.",
      tag: "tide-signin",
      requireInteraction: true,
    });
  },

  signInApproved(service) {
    return this.show("Sign-in approved", {
      body: service ? `You are signed in to ${service}.` : "Your sign-in was approved.",
      tag: "tide-signin",
    });
  },

  signInFailed(reason) {
    return this.show("Sign-in failed", {
      body: reason || "The sign-in could not be completed.",
      tag: "tide-signin",
    });
  },

  devicePaired(service) {
    return this.show("Device paired", {
      body: service ? `This device can now approve sign-ins for ${service}.` : "This device can now approve sign-ins.",
      tag: "tide-pair",
    });
  },

  /**
   * Placeholder for real Web Push. Requires a VAPID public key and a server
   * endpoint to store the subscription; neither exists in the Tide stack yet.
   * Left here so the wiring point is obvious rather than rediscovered.
   */
  async subscribeToPush(/* vapidPublicKey, saveSubscription */) {
    throw new Error("Web Push is not configured: no VAPID key or subscription endpoint");
  },
};

export { Notifications };
